import { UseFormReturn } from "react-hook-form";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import z from "zod";

import { createUseDebounce } from "../../../lib/debounce";
import { updateBidEntityQuery } from "../../../lib/hooks/api/bid/bids/update_cache/updateBidEntityQuery";
import { BidDetails } from "../../../lib/hooks/api/bid/bids/useBid";
import { useBidEntityActions } from "../../../lib/hooks/api/bid/bids/useBidEntityActions";
import { useUpdateBids } from "../../../lib/hooks/api/bid/bids/useUpdateBids";
import { BidEntityType } from "../../../lib/types/graphQLEnums";
import { diffArray } from "../../../lib/utils";

export const updateBidSchema = z.object({
  name: z.string().min(1),
  description: z.string().nullish(),
  value: z.coerce.number().nonnegative().nullish(),
  stageId: z.uuid(),
  assignedToId: z.uuid().nullish(),
  clarificationDeadline: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  submissionDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  startDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  awardDate: z.coerce
    .date()
    .transform((date) => date.toISOString())
    .nullish(),
  buyerIds: z.uuid().array(),
  supplierIds: z.uuid().array(),
  frameworkId: z.uuid().nullish(),
  noticeProcStageId: z.uuid().nullish(),
  leadId: z.uuid().nullish(),
});

export type BidUpdateFormInput = z.input<typeof updateBidSchema>;
export type BidUpdateFormOutput = z.output<typeof updateBidSchema>;

// Define which fields are entity fields
const entityFieldsSchema = updateBidSchema.pick({
  buyerIds: true,
  supplierIds: true,
  frameworkId: true,
  noticeProcStageId: true,
});

type EntityField = keyof z.infer<typeof entityFieldsSchema>;

// Get the keys at runtime from the schema's shape
const entityFieldKeys = Object.keys(entityFieldsSchema.shape) as EntityField[];

const isEntityField = (name: string): name is EntityField =>
  entityFieldKeys.includes(name as EntityField);

interface UseBidUpdateFormSubmitParams {
  bid: BidDetails;
  form: UseFormReturn<BidUpdateFormInput>;
}

/**
 * Manages form submission logic for bid updates, handling both direct field updates
 * and entity relationship changes (buyers, suppliers, frameworks).
 *
 * This hook separates two distinct update patterns:
 * 1. **Direct field updates** - Simple fields like name, status, etc. that can be
 *    patched directly via the updateBids mutation
 * 2. **Entity field updates** - Relationship fields (buyers, suppliers, frameworks)
 *    that require add/remove operations to maintain many-to-many relationships
 *
 * Most fields are debounced and submitted on change, while the `value` field
 * is submitted on blur to avoid excessive API calls during numeric input.
 */
export function useBidUpdateFormSubmit({ bid, form }: UseBidUpdateFormSubmitParams) {
  const {
    reset,
    trigger,
    getValues,
    formState: { dirtyFields },
  } = form;

  const { removeBidEntities, addBidEntities } = useBidEntityActions({ bidId: bid.id });
  const queryClient = useQueryClient();

  const { mutate: updateBids } = useUpdateBids({
    onSuccess: () => {
      toast.success("Bid updated successfully");
      // Reset dirty state while preserving current values
      reset({}, { keepValues: true });
    },
    onError: (error) => {
      console.warn("Failed to update bid", JSON.stringify(error));
      toast.error("Failed to update bid");
    },
  });

  /**
   * Handles updates for simple fields by sending only the changed field.
   */
  function handleUpdateFieldSubmit(fieldName: string, values: BidUpdateFormOutput) {
    let fieldValue = values[fieldName as keyof BidUpdateFormOutput];

    if (fieldValue === undefined) {
      return;
    }

    updateBids({
      updateBidsRequest: {
        ids: [bid.id],
        updateData: { [fieldName]: fieldValue },
      },
    });
  }

  /**
   * Handles updates for entity relationship fields (buyers, suppliers, frameworks).
   */
  async function handleEntityFieldSubmit(field: EntityField, values: BidUpdateFormOutput) {
    switch (field) {
      case "buyerIds": {
        const customBuyerIdSet = new Set(bid.customBuyers.map((b) => b.id));
        const allCurrentBuyerIds = [
          ...bid.buyers.map((b) => b.id),
          ...bid.customBuyers.map((b) => b.id),
        ];
        const { added, removed } = diffArray(allCurrentBuyerIds, values.buyerIds);

        if (removed.length > 0) {
          await removeBidEntities(
            removed.map((id) => ({
              entityId: id,
              entityType: customBuyerIdSet.has(id) ? "custom_buyer" : "buyer",
            })),
          );
        }

        // Custom buyers are created and linked via their own mutation (useCreateCustomBidOrganisation),
        // so only Stotles buyer IDs should be passed to addLeadEntities here.
        const stotlesAdded = added.filter((id) => !customBuyerIdSet.has(id));
        if (stotlesAdded.length > 0) {
          await addBidEntities(stotlesAdded.map((id) => ({ entityId: id, entityType: "buyer" })));
        }
        break;
      }

      case "supplierIds": {
        const { added, removed } = diffArray(
          bid.suppliers.map((s) => s.id),
          values.supplierIds,
        );

        if (removed.length > 0) {
          await removeBidEntities(removed.map((id) => ({ entityId: id, entityType: "supplier" })));
        }

        if (added.length > 0) {
          await addBidEntities(added.map((id) => ({ entityId: id, entityType: "supplier" })));
        }
        break;
      }

      case "frameworkId": {
        // A bid has one framework, so attaching one replaces the current one in a single write;
        // only clearing the selection is a removal
        const currentFrameworkId = bid.framework?.id ?? null;
        const newFrameworkId = values.frameworkId;

        if (!newFrameworkId && currentFrameworkId) {
          await removeBidEntities([{ entityId: currentFrameworkId, entityType: "framework" }]);
        } else if (newFrameworkId && newFrameworkId !== currentFrameworkId) {
          await addBidEntities([{ entityId: newFrameworkId, entityType: "framework" }]);
        }
        // If values match, no action needed
        break;
      }

      case "noticeProcStageId": {
        // As for the framework: a bid has one tender, so attaching one replaces the current one
        const currentNoticeId = bid.notice?.procurementStage.id ?? null;
        const newNoticeId = values.noticeProcStageId;

        if (!newNoticeId && currentNoticeId) {
          await removeBidEntities([{ entityId: currentNoticeId, entityType: "notice" }]);
        } else if (newNoticeId && newNoticeId !== currentNoticeId) {
          await addBidEntities([{ entityId: newNoticeId, entityType: "notice" }]);
          // The server drops the old notice but only reports the new one, so clear the old
          // notice's cached bid here
          if (currentNoticeId) {
            updateBidEntityQuery({
              queryClient,
              entityId: currentNoticeId,
              entityType: BidEntityType.Notice,
              bidId: bid.id,
              bidName: bid.name,
              isAdding: false,
            });
          }
        }
        // If values match, no action needed
        break;
      }

      default:
        throw new Error(`Unhandled entity field: ${field}`);
    }
  }

  const useDebounce500 = createUseDebounce(500);

  /**
   * Debounced submit handler for use with useFormAutoSubmit.
   */
  const debouncedHandleSubmit = useDebounce500(
    async (values: BidUpdateFormInput, meta: { name: string }) => {
      // Value field uses blur submission to avoid API spam during typing
      if (meta.name === "value") return;

      const parsedValues = updateBidSchema.parse(values);

      if (isEntityField(meta.name)) {
        await handleEntityFieldSubmit(meta.name, parsedValues);
      } else {
        handleUpdateFieldSubmit(meta.name, parsedValues);
      }
    },
  );

  /**
   * Blur-based submission handler specifically for the `value` field.
   */
  const submitValueUpdate = async () => {
    const isValid = await trigger("value");

    if (isValid && dirtyFields.value) {
      const parsedValues = updateBidSchema.parse(getValues());
      // An empty input clears the value (null); z.coerce would otherwise turn "" into 0.
      // A 0 is only stored when the user explicitly types "0".
      const rawValue = getValues("value");
      if (rawValue === "" || rawValue == null) {
        parsedValues.value = null;
      }
      handleUpdateFieldSubmit("value", parsedValues);
    }
  };

  return {
    debouncedHandleSubmit,
    submitValueUpdate,
  };
}
